import {FC, useCallback, useMemo, useState} from 'react';
//mui
import {Box, MenuItem, Select as MuiSelect, SelectChangeEvent, FormControl, SxProps, Theme} from '@mui/material';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//icons
import {ReactComponent as AcceptIcon} from '@icons/accept.svg';
import {ReactComponent as ArrowDownIcon} from '@icons/select-arrow-down.svg';
//types
import {DefaultCheckboxColor, SelectOptionsType} from '@/utils/types';
//helpers
import {getValidationErrorsFromArray, getValidationErrorsFromObject} from '@/utils/helpers';
//components
import InputLabel from '../Inputs/InputLabel';
import FormHelperText from '../FormHelperText';
import Loader from '../Loader';
//styles
import {multipleSelectStyle} from './MultipleSelect.styles';

interface MultipleSelectProps {
  value: string[];
  options: SelectOptionsType[];
  defaultValue?: string | string[];
  sx?: SxProps<Theme>;
  field?: FieldInputProps<string | string[]>;
  form?: FormikProps<any>;
  label?: string;
  placeholder?: string;
  id?: number;
  name?: string;
  parentClassName?: SxProps<Theme>;
  disabled?: boolean;
  checkboxType?: DefaultCheckboxColor;
  disableErrorMessage?: boolean;
  customError?: string;
  isLoading?: boolean;
  isArrayData?: boolean;
  isObjectData?: boolean;
  onChange?: (event: SelectChangeEvent, option: SelectOptionsType | SelectOptionsType[]) => void;
}

const MultipleSelect: FC<MultipleSelectProps> = ({
  value,
  options,
  field,
  form,
  label,
  defaultValue,
  id,
  disableErrorMessage,
  customError,
  parentClassName,
  checkboxType,
  placeholder,
  disabled,
  isArrayData,
  onChange,
  isLoading,
  isObjectData,
  ...rest
}) => {
  const [isSelectPopoverOpen, setIsSelectPopoverOpen] = useState(false);

  const errorText = useMemo(() => {
    if (field && form) {
      if (isArrayData) return getValidationErrorsFromArray(field, form);
      if (isObjectData) return getValidationErrorsFromObject(field, form);
      return (form.errors[field.name] && form.touched[field.name] && (form.errors[field.name] as string)) || undefined;
    }
  }, [field, form]);

  const changeHandler = (event: SelectChangeEvent) => {
    const {value} = event.target;

    const selectedOptions = options?.filter((option) => value?.includes(option.id)) as SelectOptionsType[];
    if (onChange) {
      onChange(event, selectedOptions);
    } else if (form && field) {
      const selectedIds = selectedOptions.map((option) => option.id);
      form.setFieldValue(field.name, selectedIds);
    }
  };

  const selectOptions = options?.map((item: SelectOptionsType) => (
    <MenuItem key={item.id} value={item.id} sx={multipleSelectStyle.optionItem}>
      {item.name}
      {value?.includes(item.id) && <AcceptIcon />}
    </MenuItem>
  ));

  const openPopover = () => setIsSelectPopoverOpen(true);
  const closePopover = () => setIsSelectPopoverOpen(false);

  //   const handlePopoverChange = () => setIsSelectPopoverOpen(!isSelectPopoverOpen);

  const iconArrowDown = useCallback(
    () => (
      <Box
        onClick={() => !disabled && openPopover()}
        sx={{
          ...multipleSelectStyle.icon,
          ...(!!errorText && multipleSelectStyle.iconError),
          ...(disabled && multipleSelectStyle.iconDisabled),
          ...(isSelectPopoverOpen && multipleSelectStyle.iconRotate),
          cursor: disabled ? 'default' : 'pointer',
          pointerEvents: 'auto',
        }}
      >
        {isLoading ? <Loader /> : <ArrowDownIcon />}
      </Box>
    ),
    [errorText, disabled, isSelectPopoverOpen, isLoading]
  );

  return (
    <Box sx={{...parentClassName, ...multipleSelectStyle.wrapper}}>
      {label && <InputLabel label={label} error={!!errorText} disabled={disabled} />}

      <FormControl disabled={disabled} error={!!errorText}>
        <MuiSelect
          {...rest}
          multiple
          value={value as unknown as string}
          defaultValue={defaultValue as unknown as string}
          open={isSelectPopoverOpen}
          onOpen={openPopover}
          onClose={closePopover}
          //   onOpen={handlePopoverChange}
          //   onClose={handlePopoverChange}
          label={false}
          onChange={changeHandler}
          displayEmpty
          IconComponent={iconArrowDown}
          renderValue={(selected) => {
            const selectedNames = options
              ?.filter((item: SelectOptionsType) => selected?.includes(item.id))
              .map((item) => item.name);

            if (selectedNames && selectedNames.length > 0) {
              return selectedNames.join(', ');
            }

            if (placeholder) {
              return <Box sx={{...(!errorText && multipleSelectStyle.placeholder)}}>{placeholder}</Box>;
            }

            return defaultValue;
          }}
        >
          {selectOptions}
        </MuiSelect>
      </FormControl>

      {!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
    </Box>
  );
};

export default MultipleSelect;
